import React, { useState, useEffect, useRef } from 'react';
import { UserData, AppSettings, GeneratedProfile } from '../types';
import CustomDatePicker from '../components/CustomDatePicker';
import CooldownTimer from '../components/CooldownTimer';
import { useSetMeta } from '../hooks/useSetMeta';
import { calculateZodiac, getChineseZodiac } from '../utils/calculator';
import { generateAIContent } from '../services/ai';
import AdSpace from '../components/AdSpace';
import { Sparkles, Share2, ChevronDown, Check, Wand2, Clock, ArrowRight, Loader2, Star } from 'lucide-react';
import { ZODIAC_DATA } from '../constants';
import { getTimeUntilAvailable } from '../utils/cosmic';
import personalityData from '../src/content/personality.json';

interface PersonalityPageProps {
  userData: UserData;
  settings?: AppSettings;
  updateSettings?: (s: Partial<AppSettings>) => void;
}

const TONES = ['Mystical', 'Playful', 'Professional', 'Poetic'];

const PersonalityPage: React.FC<PersonalityPageProps> = ({ userData, settings, updateSettings }) => {
  // Set SEO meta tags for this page
  useSetMeta({
    title: 'AI Personality Profile - Birth Chart Analysis & Life Purpose | ZodiAge',
    description: 'Discover your unique personality profile with AI-powered astrology analysis including zodiac traits, Chinese zodiac, and life purpose insights.',
    ogImage: 'https://zodiage.com/logo.png'
  });

  const [selectedDate, setSelectedDate] = useState<Date | null>(userData.birthDate || null);
  const [selectedTone, setSelectedTone] = useState<string>('Mystical');
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState<GeneratedProfile | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  
  const [timeUntilReset, setTimeUntilReset] = useState('');
  const [isLocked, setIsLocked] = useState(false);

  const [isToneOpen, setIsToneOpen] = useState(false);
  const toneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (settings) {
      const status = getTimeUntilAvailable(settings.lastPersonalityTimestamp || null);
        if (status.locked) {
            setIsLocked(true);
            setTimeUntilReset(status.text);
            if (settings.lastPersonalityProfile) {
                setProfile(settings.lastPersonalityProfile);
            }
        }
    }

    const updateTimer = () => {
        const status = getTimeUntilAvailable(settings?.lastPersonalityTimestamp || null);
        setIsLocked(status.locked);
        setTimeUntilReset(status.text);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [settings?.lastPersonalityTimestamp, settings?.lastPersonalityProfile]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toneRef.current && !toneRef.current.contains(event.target as Node)) {
        setIsToneOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (userData.birthDate) {
        setSelectedDate(userData.birthDate);
    }
  }, [userData.birthDate]);

  const copyTextToClipboard = async (text: string) => {
    let success = false;
    
    // 1. Try modern Clipboard API (requires secure context)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        success = true;
      } catch (err) {
        console.warn("Clipboard API failed, trying legacy fallback", err);
      }
    }

    // 2. Legacy Fallback (Textarea hack)
    if (!success) {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        success = document.execCommand('copy');
        document.body.removeChild(textArea);
      } catch (err) {
        console.error('Fallback copy failed', err);
      }
    }

    if (success) {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    }
    return success;
  };

  const handleShare = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!profile) return;
    
    const shareText = `🌟 My Cosmic Identity: ${profile.headline}\n\n"${profile.quote}"\n\nDiscover your cosmic destiny at ZodiAge: ${window.location.origin}`;

    // Prefer Native Share on Mobile/Supported browsers
    if (navigator.share) {
      try {
        await navigator.share({
          title: `ZodiAge - ${profile.headline}`,
          text: shareText,
          url: window.location.origin,
        });
        // Visual confirmation
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 3000);
        return;
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        // Fallback to clipboard if share fails
        await copyTextToClipboard(shareText);
      }
    } else {
      // Desktop / No-Native-Share fallback
      await copyTextToClipboard(shareText);
    }
  };

  const handleGenerate = async () => {
    if (!selectedDate) return;

    const status = getTimeUntilAvailable(settings?.lastPersonalityTimestamp || null);
    if (status.locked) return;

    setIsLoading(true);
    setProfile(null);

    try {
      const westernSign = calculateZodiac(selectedDate);
      const chineseSign = getChineseZodiac(selectedDate);
      const birthYear = selectedDate.getFullYear();

      const prompt = `
        Generate a detailed astrological personality profile for a person born on ${selectedDate.toDateString()}.
        Western Zodiac: ${westernSign}.
        Chinese Zodiac: ${chineseSign}.
        Birth Year: ${birthYear}.
        Tone of voice: ${selectedTone}.
        
        The response must be valid JSON matching the schema.
        - 'headline': A short, majestic title for this combination (e.g., "The Majestic Architect").
        - 'quote': A profound or catchy quote representing their essence.
        - 'luckyColor': A specific color name (e.g. "Sapphire Blue").
        - 'luckyNumber': A single integer.
        - 'traits': An array of 5 adjective strings (short).
        - 'socialBlurb': A short, shareable paragraph (approx 40 words) summarizing their vibe, including emojis.
        - 'analysis': A deep, 3-paragraph psychological and spiritual analysis of how the Western ${westernSign} and Chinese ${chineseSign} energies combine.
      `;

      const responseText = await generateAIContent(prompt, true);

      if (responseText) {
        const data = JSON.parse(responseText) as GeneratedProfile;
        setProfile(data);
        if (updateSettings) {
            updateSettings({
                lastPersonalityTimestamp: Date.now(),
                lastPersonalityProfile: data
            });
        }
      }

    } catch (error) {
      console.error("Generation failed", error);
    } finally {
      setIsLoading(false);
    }
  };

  const westernSign = selectedDate ? calculateZodiac(selectedDate) : null;
  const chineseSign = selectedDate ? getChineseZodiac(selectedDate) : null;
  const symbol = westernSign ? ZODIAC_DATA[westernSign].symbol : '';

  if (!userData.birthDate) {
    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 animate-in fade-in duration-500">
            <div className="bg-card rounded-2xl p-8 border border-gray-800 shadow-2xl text-center max-w-md w-full">
                <div className="w-16 h-16 bg-purple-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Wand2 className="text-secondary" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">Begin Your Journey</h2>
                <p className="text-gray-400 mb-8 leading-relaxed">
                    To unlock your cosmic personality profile, we first need to anchor you in time. Please calculate your age to proceed.
                </p>
                <button 
                    onClick={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}
                    className="w-full bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white font-bold py-3.5 rounded-full hover:from-purple-500 hover:via-purple-400 hover:to-pink-400 transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-600/50 hover:shadow-purple-500/70"
                >
                    Go to Calculator <ArrowRight size={18} />
                </button>
            </div>

        </div>
    );
  }

  return (

    <div className="space-y-8 animate-in fade-in duration-500 pb-12">

            <div id="personality-generator-section" className="bg-card rounded-2xl p-8 border border-gray-800 shadow-2xl relative">
        <div className="mb-6 relative z-10">
          <div className="flex justify-between items-start">
              <div>
                  <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <Wand2 className="text-secondary" size={24} /> Zodiac Personality Generator
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Discover the cosmic secrets of your personality with AI.
                  </p>
              </div>
              {isLocked && (
                 <div className="flex items-center gap-2 text-xs font-mono text-white bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 px-3 py-1.5 rounded-full shadow-lg shadow-purple-600/50 animate-in fade-in">
                     <Clock size={12} className="animate-pulse" /> Next use in {timeUntilReset}
                 </div>
              )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="space-y-2">
                <label className="text-sm text-gray-400 font-medium">Birth Date</label>
                <CustomDatePicker value={selectedDate} onChange={(d) => setSelectedDate(d)} />
            </div>
            <div className="space-y-2" ref={toneRef}>
                <label className="text-sm text-gray-400 font-medium">Personality Tone</label>
                <div className="relative">
                    <button 
                        onClick={() => setIsToneOpen(!isToneOpen)}
                        className="w-full bg-input border border-gray-700 rounded-lg p-3 text-white flex items-center justify-between hover:border-secondary transition-all h-[50px]"
                    >
                        <span>{selectedTone}</span>
                        <ChevronDown size={18} className={`transition-transform duration-300 ${isToneOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isToneOpen && (
                        <div className="absolute top-full left-0 w-full mt-2 bg-card border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95">
                            {TONES.map(t => (
                                <div 
                                    key={t}
                                    onClick={() => { setSelectedTone(t); setIsToneOpen(false); }}
                                    className="p-3 hover:bg-gray-800 cursor-pointer text-gray-300 flex items-center justify-between group"
                                >
                                    {t}
                                    {selectedTone === t && <Check size={16} className="text-secondary" />}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>

        {/* In-Article Ad for Personality Page */}
        <AdSpace type="in-article" slot="7716101506" className="my-8" />

        <button 
            onClick={handleGenerate}
            disabled={isLoading || isLocked || !selectedDate}
            className="w-full bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-purple-600/50 hover:shadow-purple-500/70 hover:from-purple-500 hover:via-purple-400 hover:to-pink-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:from-purple-700 disabled:to-pink-600 group"
        >
            {isLoading ? <Loader2 className="animate-spin" size={24} /> : <><Sparkles size={20} className="group-hover:animate-pulse" /> Generate Cosmic Profile</>}
        </button>
      </div>

      {profile && westernSign && (
        <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 space-y-6">
            <div className="bg-card rounded-3xl p-8 border border-gray-800 shadow-2xl relative overflow-hidden">
                <div className="flex justify-between items-start mb-8 relative z-10 border-b border-gray-800 pb-6">
                    <div>
                        <h2 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                             Your Cosmic Profile: {westernSign} <span className="text-secondary bg-secondary/10 px-2 py-0.5 rounded-lg text-2xl">{symbol}</span>
                        </h2>
                        <p className="text-gray-400 text-sm">Chinese Zodiac: <span className="text-white font-medium">{chineseSign}</span></p>
                    </div>
                    
                    <button 
                      onClick={handleShare}
                      className={`p-2.5 px-6 rounded-full transition-all border flex items-center gap-2 shadow-lg active:scale-95 ${isCopied ? 'bg-gradient-to-r from-green-600 to-green-500 border-green-400 text-white shadow-green-600/50' : 'bg-gradient-to-r from-purple-700 to-pink-600 hover:from-purple-600 hover:to-pink-500 text-white border-purple-500/50 shadow-purple-600/40'}`}
                      title="Share Profile"
                    >
                        {isCopied ? <Check size={18} /> : <Share2 size={18} />}
                        <span className="text-[10px] font-black uppercase tracking-widest">
                            {isCopied ? 'Copied!' : 'Share'}
                        </span>
                    </button>
                </div>

                <div className="text-center mb-10">
                    <h3 className="text-4xl font-display font-bold text-white mb-3 tracking-tight">{profile.headline}</h3>
                    <p className="text-secondary italic text-xl font-medium px-4 opacity-90 leading-relaxed">"{profile.quote}"</p>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-[#020410] p-5 rounded-2xl border border-gray-800 text-center shadow-inner">
                        <div className="text-[10px] text-gray-500 uppercase tracking-[0.2em] font-bold mb-1">Lucky Color</div>
                        <div className="text-white font-bold text-lg">{profile.luckyColor}</div>
                    </div>
                    <div className="bg-[#020410] p-5 rounded-2xl border border-gray-800 text-center shadow-inner">
                        <div className="text-[10px] text-gray-500 uppercase tracking-[0.2em] font-bold mb-1">Lucky Number</div>
                        <div className="text-white font-bold text-2xl text-secondary">{profile.luckyNumber}</div>
                    </div>
                    <div className="bg-[#020410] p-5 rounded-2xl border border-gray-800 text-center col-span-2 shadow-inner">
                        <div className="text-[10px] text-gray-400 uppercase tracking-[0.2em] font-bold mb-2">Core Traits</div>
                        <div className="flex flex-wrap justify-center gap-2">
                            {profile.traits.map(t => (
                                <span key={t} className="bg-white/10 text-white text-[10px] font-black px-3 py-1 rounded-full border border-white/20 uppercase tracking-widest">{t}</span>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="prose prose-invert max-w-none">
                    <div className="bg-secondary/5 border-l-4 border-secondary p-5 mb-10 rounded-r-2xl italic text-gray-300 leading-relaxed text-sm shadow-lg">
                        <Sparkles size={16} className="text-secondary mb-2" />
                        {profile.socialBlurb}
                    </div>
                    <div className="space-y-6 text-gray-300 leading-loose whitespace-pre-line text-base font-light">
                        {profile.analysis}
                    </div>
                </div>

                <div className="mt-12 pt-8 border-t border-gray-800 flex justify-center">
                    <button 
                        onClick={handleShare}
                        className={`flex items-center gap-3 transition-all text-[10px] font-black uppercase tracking-[0.4em] active:scale-95 ${isCopied ? 'text-green-400' : 'text-secondary hover:text-white'}`}
                    >
                        {isCopied ? <Check size={14} /> : <Share2 size={14} />} 
                        {isCopied ? 'Destiny Copied!' : 'Share This Destiny'}
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* Educational Content (AdSense Compliance) - Rendered from JSON */}
      <div className="w-full max-w-5xl mx-auto px-4 mb-12 mt-16">
          <div className="bg-cosmic-800/30 p-8 md:p-12 rounded-2xl border border-cosmic-700/50">
              <div className="seo-content" dangerouslySetInnerHTML={{ __html: personalityData.html }} />
          </div>
      </div>
    </div>
  );
};

export default PersonalityPage;
